import { z } from 'zod';

import { baseFetch } from '@/infra/http/base';

/**
 * The slice of `GET public/externships` the hand-off cards need. That endpoint is the platform API's
 * own logged-out catalog (the externship preview widget already reads it), so it carries the real
 * title, company, logo, cover image, length and career-focus categories with no session.
 *
 * Lenient on purpose: one odd record must not cost the visitor all three cards, so every optional
 * field falls back rather than failing the parse.
 */
const PublicExternshipSchema = z.object({
  companyLogo: z.string().nullish().catch(null),
  companyName: z.string().nullish().catch(null),
  details: z
    .object({ topImage: z.string().nullish().catch(null) })
    .nullish()
    .catch(null),
  id: z.number(),
  tags: z
    .object({ categories: z.array(z.string()).nullish().catch(null) })
    .nullish()
    .catch(null),
  title: z.string().min(1),
  weeksDuration: z.coerce.number().nullish().catch(null),
});

export interface PublicCatalogExternship {
  categories: readonly string[];
  company: string | null;
  id: number;
  image: string | null;
  logo: string | null;
  title: string;
  weeks: number | null;
}

/** Null on any failure, so the panel falls back to its static table of real externships. */
export async function getPublicExternshipCatalog(): Promise<PublicCatalogExternship[] | null> {
  try {
    const response = await baseFetch.get('public/externships').json();
    const parsed = z.array(z.unknown()).parse(response);

    return parsed.flatMap((entry) => {
      const result = PublicExternshipSchema.safeParse(entry);
      if (!result.success) {
        return [];
      }
      const { companyLogo, companyName, details, id, tags, title, weeksDuration } = result.data;
      return [
        {
          categories: tags?.categories ?? [],
          company: companyName?.trim() || null,
          id,
          image: details?.topImage?.trim() || null,
          logo: companyLogo?.trim() || null,
          title: title.trim(),
          weeks: weeksDuration && weeksDuration > 0 ? weeksDuration : null,
        },
      ];
    });
  } catch {
    return null;
  }
}
